/**
 * Customização AgSUS de @plone/volto/components/theme/Login/Login.
 *
 * Diferenças em relação ao core (18.33.1):
 * 1. Apresentação no DS da AgSUS: cartão creme com a faixa amarela da marca,
 *    rótulo acima de cada campo e botões com texto ("Entrar" e "Cancelar"),
 *    usando as classes do DS (.field-label, .input, .btn). Regras em login.css.
 * 2. Textos em português, fixos aqui: a tradução do Volto não é carregada e a
 *    tela saía em inglês. O que muda entre portal e intranet (rótulo do
 *    usuário e chamada) fica em textos.js.
 * 3. A falha de login aparece dentro do cartão (role="alert"), no lugar do
 *    aviso flutuante (toast).
 * 4. Boas-vindas opcionais (textos.boasVindas, hoje só na intranet): saudação
 *    e detalhes ao lado do cartão; no celular, saudação, cartão e detalhes.
 * A lógica de entrada, saída e retorno à página de origem é a do core.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import config from '@plone/volto/registry';
import {
  login,
  logout,
  resetLoginRequest,
} from '@plone/volto/actions/userSession/userSession';
import { purgeMessages } from '@plone/volto/actions/messages/messages';
import { toast } from 'react-toastify';

import textos from './textos';
import './login.css';

const Login = (props) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const error = useSelector((state) => state.userSession.login.error);
  const loading = useSelector((state) => state.userSession.login.loading);
  const returnUrl =
    qs.parse(props.location?.search ?? location.search).return_url ||
    location.pathname.replace(/\/[^/]*\/?$/, '') ||
    '/';
  const previousToken = usePrevious(token);

  useEffect(() => {
    if (location?.state?.isLogout) {
      // Saída de verdade: cobre o caso de um token de outro backend chegar
      // até aqui vindo de um logout (ver Unauthorized.jsx no core).
      dispatch(logout());
      dispatch(purgeMessages());
      history.push(`${location.pathname}${location.search}`);
    } else if (token && token !== previousToken) {
      // Acabou de entrar.
      history.push(returnUrl || '/');
      if (toast.isActive('loggedOut')) {
        toast.dismiss('loggedOut');
      }
    }
    if (error && toast.isActive('loggedOut')) {
      toast.dismiss('loggedOut');
    }
    return () => {
      // Ao sair da tela com uma falha na memória, limpa a falha, para ela não
      // reaparecer na próxima visita.
      if (error) {
        dispatch(resetLoginRequest());
      }
    };
  }, [
    dispatch,
    token,
    error,
    history,
    returnUrl,
    location.search,
    location.pathname,
    location?.state?.isLogout,
    previousToken,
  ]);

  const boasVindas = textos.boasVindas;
  // Com boas-vindas, o título da página é a saudação (h1) e o do cartão, h2.
  const TituloCartao = boasVindas ? 'h2' : 'h1';

  const onLogin = (event) => {
    dispatch(
      login(
        document.getElementsByName('login')[0].value,
        document.getElementsByName('password')[0].value,
      ),
    );
    event.preventDefault();
  };

  return (
    <div
      id="page-login"
      className={boasVindas ? 'ag-login ag-login--boas-vindas' : 'ag-login'}
    >
      <Helmet title="Entrar" />
      {boasVindas && (
        <div className="ag-login__saudacao">
          <h1 className="ag-login__boas-vindas">{boasVindas.titulo}</h1>
          <p className="ag-login__boas-vindas-lead">{boasVindas.saudacao}</p>
        </div>
      )}
      <div className="ag-login__card">
        <TituloCartao className="ag-login__title">Entrar</TituloCartao>
        <p className="ag-login__lead">{textos.chamada}</p>

        {error && (
          <div className="ag-login__erro" role="alert">
            <strong>Não foi possível entrar.</strong> Confira o{' '}
            {textos.rotuloUsuario.toLowerCase()} e a senha: maiúsculas e
            minúsculas fazem diferença, e o Caps Lock precisa estar desligado.
          </div>
        )}

        <form method="post" onSubmit={onLogin} className="ag-login__form">
          <div className="ag-login__campo">
            <label className="field-label" htmlFor="login">
              {textos.rotuloUsuario}
            </label>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input
              className="input"
              id="login"
              name="login"
              type="text"
              inputMode={textos.teclado}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              autoFocus
            />
          </div>

          <div className="ag-login__campo">
            <label className="field-label" htmlFor="password">
              Senha
            </label>
            <input
              className="input"
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
          </div>

          <p className="ag-login__links">
            <Link to="/passwordreset">Esqueci minha senha</Link>
            {config.settings.showSelfRegistration && (
              <Link to="/register">Criar conta</Link>
            )}
          </p>

          <div className="ag-login__acoes">
            <button
              type="submit"
              id="login-form-submit"
              className="btn btn--primary btn--lg"
              disabled={loading}
              aria-busy={loading || undefined}
            >
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
            <Link
              id="login-form-cancel"
              to="/"
              className="btn btn--ghost btn--lg"
            >
              Cancelar
            </Link>
          </div>
        </form>
      </div>
      {boasVindas && (
        <div className="ag-login__detalhes">
          {boasVindas.paragrafos.map((paragrafo) => (
            <p key={paragrafo}>{paragrafo}</p>
          ))}
          {boasVindas.lista && (
            <>
              <h2 className="ag-login__detalhes-titulo">
                {boasVindas.lista.titulo}
              </h2>
              <ul>
                {boasVindas.lista.itens.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          )}
          {boasVindas.ajuda && (
            <p className="ag-login__ajuda">{boasVindas.ajuda}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Login;
